import { createSlice } from "@reduxjs/toolkit";
import { IAddGateEntery } from "src/common/typings/vehicle";
import { RootState } from "../store";
import {
  handleAddGateEntery,
  // getRolesAction,
  handleAddGateEnteryFail,
  addGateEnteryAction,
  getAllGateDocuments,
  handleGetAllGateDocsSuccess,
  handleGetAllGateDocsFail,
  createVehicleInspectionScanner,
  createVehicleInspectionWeight,
  getLocationAtTerminal,
  getVehicleDetailsByIdAction,
  uploadVehicleScanDocs,
  uploadVehicleWeightDocs,
  createPreviousGateEntryDocs,
  getWeightScanExistingDocuments,
  updateVehicleInspectionScanner,
  updateVehicleInspectionWeight,
  markVehicleEntryAsReview,
  markVehicleEntryAsReject,
  markVehicleEntryAsWrong,
  getNodVehicleDetailsByIdAction,
  addMultiContainerGateEntryAction,
  handleAddMultiContainerGateEntryFail,
  handleAddMultiContainerGateEntry,
  getAllMultiContainerGateDocuments,
  handleGetAllMultiContainerGateDocsSuccess,
  handleGetAllMultiContainerGateDocsFail,
} from "./actions/vehicleActions";

export interface VehicleState {
  vehicles: IAddGateEntery[];
  gatePassDocuments: [];
  loader: true | false;
  status: "idle" | "loading" | "failed";
  vehicleInspectionList: [];
  locationAtTerminal: [];
  currentSelectedVehicleId: number;
  currentSelectedVehicleScanId: number;
  currentSelectedVehicleWeightId: number;
  isNodWeightFile?: boolean;
  currentSelectedVehicleRegNo: number;
  currentSelectedGatePassId: number;
  existingGateEntryDocs: [];
  vehicleDetails: [];
  nodVehicleDetails: [];
  expandNextSection: boolean;
}

const initialState: VehicleState = {
  loader: false,
  status: "idle",
  vehicles: [],
  gatePassDocuments: [],
  vehicleInspectionList: [],
  locationAtTerminal: [],
  currentSelectedVehicleId: 0,
  currentSelectedVehicleScanId: 0,
  currentSelectedVehicleWeightId: 0,
  isNodWeightFile: false,
  currentSelectedVehicleRegNo: 0,
  currentSelectedGatePassId: 0,
  existingGateEntryDocs: [],
  vehicleDetails: [],
  nodVehicleDetails: [],
  expandNextSection: false,
};

export const vehicleSlice = createSlice({
  name: "vehicle",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // loginActionSync: handleLogin,
    setSelectedVehicleId: (state: VehicleState, action: any) => {
      state.currentSelectedVehicleId = action.payload;
    },
    setSelectedVehicleScanId: (state: VehicleState, action: any) => {
      state.currentSelectedVehicleScanId = action.payload.scanId;
      state.currentSelectedVehicleRegNo = action.payload.regNo;
      state.isNodWeightFile = action.payload.isNodWeightFile;
    },
    setSelectedVehicleWeightId: (state: VehicleState, action: any) => {
      state.currentSelectedVehicleWeightId = action.payload.weightId;
      state.currentSelectedVehicleRegNo = action.payload.regNo;
      state.isNodWeightFile = action.payload.isNodWeightFile;
    },
    setSelectedGatePassId: (state: VehicleState, action: any) => {
      state.currentSelectedGatePassId = action.payload;
    },
    setExistingGateEntryDocs: (state: VehicleState, action: any) => {
      state.existingGateEntryDocs = action.payload;
    },
    setExpandNextSection: (state: VehicleState, action: any) => {
      state.expandNextSection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addGateEnteryAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addGateEnteryAction.fulfilled, handleAddGateEntery)
      .addCase(addGateEnteryAction.rejected, handleAddGateEnteryFail)

      .addCase(addMultiContainerGateEntryAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addMultiContainerGateEntryAction.fulfilled, handleAddMultiContainerGateEntry)
      .addCase(addMultiContainerGateEntryAction.rejected, handleAddMultiContainerGateEntryFail)

      .addCase(getAllGateDocuments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllGateDocuments.fulfilled, handleGetAllGateDocsSuccess)
      .addCase(getAllGateDocuments.rejected, handleGetAllGateDocsFail)

      .addCase(getAllMultiContainerGateDocuments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        getAllMultiContainerGateDocuments.fulfilled,
        handleGetAllMultiContainerGateDocsSuccess
      )
      .addCase(getAllMultiContainerGateDocuments.rejected, handleGetAllMultiContainerGateDocsFail)

      .addCase(createVehicleInspectionScanner.pending, (state) => {
        state.loader = true;
        state.status = "loading";
      })
      .addCase(createVehicleInspectionScanner.fulfilled, (state) => {
        state.loader = false;
        state.status = "idle";
      })
      .addCase(createVehicleInspectionScanner.rejected, (state) => {
        state.loader = false;
        state.status = "idle";
      })

      .addCase(createVehicleInspectionWeight.pending, (state) => {
        state.loader = true;
        state.status = "loading";
      })
      .addCase(createVehicleInspectionWeight.fulfilled, (state) => {
        state.loader = false;
        state.status = "idle";
      })
      .addCase(createVehicleInspectionWeight.rejected, (state) => {
        state.loader = false;
        state.status = "idle";
      })

      .addCase(getLocationAtTerminal.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getLocationAtTerminal.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getLocationAtTerminal.fulfilled, (state, { payload }) => {
        state.status = "idle";
        state.locationAtTerminal = payload;
      });
    // Get getVehicleDetailsByIdAction methods
    builder.addCase(getVehicleDetailsByIdAction.pending, (state) => {
      state.loader = true;
      state.status = "loading";
    });
    builder.addCase(getVehicleDetailsByIdAction.rejected, (state) => {
      state.loader = false;
      state.status = "idle";
    });
    builder.addCase(getVehicleDetailsByIdAction.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.status = "idle";
      state.vehicleDetails = payload;
    });
    builder.addCase(getNodVehicleDetailsByIdAction.pending, (state) => {
      state.loader = true;
      state.status = "loading";
    });
    builder.addCase(getNodVehicleDetailsByIdAction.rejected, (state) => {
      state.loader = false;
      state.status = "idle";
    });
    builder.addCase(getNodVehicleDetailsByIdAction.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.status = "idle";
      state.nodVehicleDetails = payload;
    });
    // ./getVehicleDetailsByIdAction methods

    // Get uploadVehicleScanDocs methods
    builder.addCase(uploadVehicleScanDocs.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(uploadVehicleScanDocs.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(uploadVehicleScanDocs.fulfilled, (state) => {
      state.loader = false;
    });
    // ./uploadVehicleScanDocs methods

    // Get uploadVehicleWeightDocs methods
    builder.addCase(uploadVehicleWeightDocs.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(uploadVehicleWeightDocs.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(uploadVehicleWeightDocs.fulfilled, (state) => {
      state.loader = false;
    });
    builder.addCase(markVehicleEntryAsReview.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(markVehicleEntryAsReview.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(markVehicleEntryAsReview.fulfilled, (state) => {
      state.loader = false;
    });
    builder.addCase(markVehicleEntryAsReject.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(markVehicleEntryAsReject.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(markVehicleEntryAsReject.fulfilled, (state) => {
      state.loader = false;
    });
    builder.addCase(markVehicleEntryAsWrong.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(markVehicleEntryAsWrong.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(markVehicleEntryAsWrong.fulfilled, (state) => {
      state.loader = false;
    });
    // ./uploadVehicleWeightDocs methods

    // Get createPreviousGateEntryDocs methods
    builder.addCase(createPreviousGateEntryDocs.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(createPreviousGateEntryDocs.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(createPreviousGateEntryDocs.fulfilled, (state) => {
      state.loader = false;
    });
    // ./createPreviousGateEntryDocs methods

    // Get getWeightScanExistingDocuments methods
    builder.addCase(getWeightScanExistingDocuments.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getWeightScanExistingDocuments.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getWeightScanExistingDocuments.fulfilled, (state) => {
      state.loader = false;
    });
    // ./getWeightScanExistingDocuments methods

    // Get updateVehicleInspectionScanner methods
    builder.addCase(updateVehicleInspectionScanner.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(updateVehicleInspectionScanner.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(updateVehicleInspectionScanner.fulfilled, (state) => {
      state.loader = false;
    });
    // ./updateVehicleInspectionScanner methods

    // Get updateVehicleInspectionWeight methods
    builder.addCase(updateVehicleInspectionWeight.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(updateVehicleInspectionWeight.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(updateVehicleInspectionWeight.fulfilled, (state) => {
      state.loader = false;
    });
    // ./updateVehicleInspectionWeight methods
  },
});

// export const { loginActionSync } = authSlice.actions;
export const {
  setSelectedVehicleId,
  setSelectedVehicleScanId,
  setSelectedVehicleWeightId,
  setSelectedGatePassId,
  setExistingGateEntryDocs,
  setExpandNextSection,
} = vehicleSlice.actions; // to Acces Reducers methods

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const loader = (state: RootState) => state.vehicle.loader;
export const selectStatus = (state: RootState) => state.vehicle.status;
export const selectVehicles = (state: RootState) => state.vehicle.vehicles;
export const selectDocuments = (state: RootState) => state.vehicle.gatePassDocuments;
export const currentSelectedVehicleId = (state: RootState) =>
  state.vehicle.currentSelectedVehicleId;
export const currentSelectedVehicleScanId = (state: RootState) =>
  state.vehicle.currentSelectedVehicleScanId;
export const currentSelectedVehicleWeightId = (state: RootState) =>
  state.vehicle.currentSelectedVehicleWeightId;
export const currentSelectedIsNodWeightFile = (state: RootState) => state.vehicle.isNodWeightFile;
export const currentSelectedVehicleRegNo = (state: RootState) =>
  state.vehicle.currentSelectedVehicleRegNo;
export const currentSelectedGatePassId = (state: RootState) =>
  state.vehicle.currentSelectedGatePassId;
export const expandNextSection = (state: RootState) => state.vehicle.expandNextSection;
export const existingGateEntryDocs = (state: RootState) => state.vehicle.existingGateEntryDocs;
export const selectVehicleDetails = (state: RootState) => state.vehicle.vehicleDetails;
export const selectNodVehicleDetails = (state: RootState) => state.vehicle.nodVehicleDetails;
export const selectLocationAtTerminal = (state: RootState) => state.vehicle.locationAtTerminal;
